import React, { useEffect, useState } from 'react';
import './style.css';
import { Modal, Form, Pagination } from 'react-bootstrap';
import TabelaDinamica from '../tabela_dinamica';
import { useApi } from '../../hooks/ApiGateway';
import { useParams } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';

interface TableColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  type?: 'percentage' | 'number';
  format?: boolean;
  colorCondition?: Record<string, string>;
}

interface TableData {
  columns: TableColumn[];
  rows: Record<string, any>[];
}

interface TableConfig {
  search?: {
    enabled: boolean;
    placeholder?: string;
  };
  pagination?: {
    enabled: boolean;
    perPage?: number;
  };
  api?: {
    url: string;
    method?: 'GET' | 'POST';
    headers?: Record<string, string>;
  };
}

type DataType = {
  body: any[];
};

const TabelaElegibilidade = ({ data, config }: { data: TableData; config: TableConfig }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any[]>([]);
  const { makeGetRequest } = useApi<DataType>();
  const { id } = useParams();

  const fetchDataRejeitados = async () => {
    const result = await makeGetRequest(
      `${API_CONFIG.BASE_URL}/cessao/detalhe/base-rejeitada/detalhes-contratos?id_remessa=${id}`
    );
    const responseData = JSON.parse(result.body);
    setSelectedRow(responseData);
    setShowModal(true);
    setModalPage(1); // reset na página do modal ao abrir
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const [filterStatus, setFilterStatus] = useState('');
  const [filterDocumento, setFilterDocumento] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const itemsPerPage = 10;

  const statusOptions = Array.from(new Set(selectedRow?.map((item) => item.STATUS)));

  const filteredModalRows = selectedRow?.filter(
    (item) =>
      item.NUMERO_DOCUMENTO.toLowerCase().includes(filterDocumento.toLowerCase()) &&
      (filterStatus === '' || item.STATUS === filterStatus)
  );

  const paginatedModalRows = filteredModalRows?.slice(
    (modalPage - 1) * itemsPerPage,
    modalPage * itemsPerPage
  );

  const totalModalPages = Math.ceil((filteredModalRows?.length || 0) / itemsPerPage);

  const renderPagination = () => {
    let items = [];
  
    const maxPagesToShow = 5;
    let startPage = Math.max(1, modalPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
  
    if (endPage > totalModalPages) {
      endPage = totalModalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
  
    items.push(
      <Pagination.First onClick={() => setModalPage(1)} disabled={modalPage === 1} key="first" />
    );
    items.push(
      <Pagination.Prev
        onClick={() => setModalPage((prev) => Math.max(prev - 1, 1))}
        disabled={modalPage === 1}
        key="prev"
      />
    );
  
    if (startPage > 1) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
  
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === modalPage}
          onClick={() => setModalPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
  
    if (endPage < totalModalPages) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    }
  
    items.push(
      <Pagination.Next
        onClick={() => setModalPage((prev) => Math.min(prev + 1, totalModalPages))}
        disabled={modalPage === totalModalPages}
        key="next"
      />
    );
    items.push(
      <Pagination.Last
        onClick={() => setModalPage(totalModalPages)}
        disabled={modalPage === totalModalPages}
        key="last"
      />
    );
  
    return <Pagination className="justify-content-center mt-3 flex-wrap">{items}</Pagination>;
  };
  

  useEffect(() => {
    setModalPage(1);
  }, [filterStatus, filterDocumento]);
  

  return (
    <>
      <TabelaDinamica
        data={data}
        config={config}
        title="Transações rejeitadas"
        subtitle="Utilize o campo abaixo para pesquisar por um critério específico"
        button={true}
        TextButton="Ver detalhes"
        commandButton={() => fetchDataRejeitados()}
      />

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" dialogClassName="modal-rejeicoes">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Rejeição</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex mb-3 gap-3">
            <Form.Control
              type="text"
              placeholder="Filtrar por Nº Documento"
              value={filterDocumento}
              onChange={(e) => setFilterDocumento(e.target.value)}
            />
            <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Todos os Status</option>
              {statusOptions.map((status, idx) => (
                <option key={idx} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Número do Documento</th>
                  <th>Valor de Aquisição</th>
                  <th>Valor Nominal</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(paginatedModalRows) && paginatedModalRows.length > 0 ? (
                  paginatedModalRows.map((item, index) => (
                    <tr key={index}>
                      <td>{item.NUMERO_DOCUMENTO}</td>
                      <td>{formatCurrency(item.VALOR_PRESENTE)}</td>
                      <td>{formatCurrency(item.VALOR_NOMINAL)}</td>
                      <td>{item.STATUS}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">
                      Nenhum resultado encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalModalPages > 1 && (
            <div className="mt-3" style={{ overflowX: 'auto' }}>
              {renderPagination()}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TabelaElegibilidade;
